import React, { useState } from 'react';
import { ImageSelector } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';
import { PATCH_LETTER_A, PATCH_BADDIE, PATCH_SIGN } from './patchDemoData';
import capUppsala from '@ajat/design-system/assets/crownstudent/configurator/models/uppsala.png';
import capModern from '@ajat/design-system/assets/crownstudent/configurator/models/modern.png';
import capLunda from '@ajat/design-system/assets/crownstudent/configurator/models/lunda.png';

export const meta = {
  component: ImageSelector,
  title: 'Image Selector',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '4290:24169',
  description:
    'Selectable 120px image tile — a letter, motif or style option — with a title and a label-size price. Selected draws a gray-10 border; hover is desktop-pointer only. Renders as a button with aria-pressed. `variant="compact"` is the Figma V2 option card (Crown Student cap models): sized by its row — a column with a 40px image on mobile, a row with a 52px image from 1024px.',
  props: [
    ['image', 'string', '—', 'Option image — contained with a 12.5% inset on white'],
    ['imageAlt', 'string', "''", 'Alt text for the image'],
    ['title', 'string', '—', 'Option name; the details block renders when title or price is set'],
    ['price', 'string', '—', 'Formatted price — DS Price at label size'],
    ['selected', 'bool', 'false', 'Selected state — gray-10 border'],
    ['imageFit', "'contain' | 'cover'", "'contain'", 'contain: cut-out artwork with the inset; cover: a photo filling the well. Width: --cfg-image-selector-w (120px)'],
    ['variant', "'tile' | 'compact'", "'tile'", 'tile: the 120px tile; compact: the Figma V2 option card — a column (40px image) on mobile, a row (52px image) from 1024px, sized by its row'],
    ['onClick', 'func', '—', 'Click handler'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Single-select row',
      code: `import { ImageSelector } from '@ajat/design-system/Configurator';

<div className="flex flex-wrap gap-medium-s">
  {options.map((option) => (
    <ImageSelector
      key={option.id}
      image={option.image}
      imageAlt={option.name}
      title={option.name}
      price={option.priceLabel}
      selected={option.id === selectedId}
      onClick={() => setSelectedId(option.id)}
    />
  ))}
</div>`,
    },
  ],
};

const CAP_MODELS = [
  { id: 'uppsala', image: capUppsala, name: 'Uppsala' },
  { id: 'modern', image: capModern, name: 'Modern' },
  { id: 'lunda', image: capLunda, name: 'Lunda' },
];

const OPTIONS = [
  { id: 'a', image: PATCH_LETTER_A, name: 'Letter A', price: '49 kr' },
  { id: 'baddie', image: PATCH_BADDIE, name: 'Baddie', price: '49 kr' },
  { id: 'sign', image: PATCH_SIGN, name: 'Sign', price: '39 kr' },
];

export default function ImageSelectorPage() {
  const [selectedId, setSelectedId] = useState('a');
  const [capModel, setCapModel] = useState('uppsala');

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States" hint="hover for the Hover state">
        <DemoRow>
          <Demo caption="DEFAULT"><ImageSelector image={PATCH_LETTER_A} imageAlt="Letter A" title="Baddie" price="49 kr" /></Demo>
          <Demo caption="SELECTED"><ImageSelector image={PATCH_LETTER_A} imageAlt="Letter A" title="Baddie" price="49 kr" selected /></Demo>
          <Demo caption="NO DETAILS"><ImageSelector image={PATCH_LETTER_A} imageAlt="Letter A" /></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Single-select row" hint="click to select">
        <div className="flex flex-wrap gap-medium-s">
          {OPTIONS.map((option) => (
            <ImageSelector
              key={option.id}
              image={option.image}
              imageAlt={option.name}
              title={option.name}
              price={option.price}
              selected={option.id === selectedId}
              onClick={() => setSelectedId(option.id)}
            />
          ))}
        </div>
      </DemoCard>

      <DemoCard title="Compact (V2)" hint="three equal columns — a row from 1024px, a column below">
        <div className="grid gap-small-l" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', maxWidth: 480 }}>
          {CAP_MODELS.map((model) => (
            <ImageSelector
              key={model.id}
              variant="compact"
              image={model.image}
              imageAlt={model.name}
              title={model.name}
              selected={model.id === capModel}
              onClick={() => setCapModel(model.id)}
            />
          ))}
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
